// Opportunities in der linken Spalte der Lead-Seite (Runde 7B, L36/L37): anlegen mit „+“, ändern mit dem Stift, Status direkt an der
// Pille wechseln (auch auf Gewonnen und Verloren). Der Dialog stammt aus dem Opportunity-Modul (5D); Speichern geht mit dem Schlüssel
// des Nutzers nach Close. Löschen gibt es nicht: der Close-Zugang des CRM löscht keine Opportunities.
// Seit 18.10.2026: Kontakt, Zuständig, Wert, Wahrscheinlichkeit, Abschlussdatum und Notiz sind wie die Lead-Felder direkt anklickbar
// (Klick öffnet die Eingabe, Enter oder Klick woanders speichert, Esc verwirft, keine Nachfrage); der Status zeigt alle Pipelines.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { FeldWert, Kontakt, Opportunity } from "../../api";
import { opportunitiesApi, type OpportunityAenderung, type OpportunityZeile } from "../../api/opportunities";
import { useMeta } from "../../daten/meta";
import { OpportunityDialog } from "../../opportunities";
import { useDarfAendern, useStatusWechsel } from "../../opportunities/statuswechsel";
import { datumText } from "../../werkzeuge/zeit";
import { T } from "../../texte";
import { istLeer, type FeldDef } from "../../werkzeuge/felder";
import { FeldEingabe } from "../FeldEingabe";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { MenueKnopf, type MenuePunkt } from "../Menue";
import { Abschnitt } from "./Abschnitt";
import { TL } from "./leadTexte";
import "./leadTiefe.css";

const EURO = (waehrung: string) => new Intl.NumberFormat("de-DE", { style: "currency", currency: waehrung || "EUR", maximumFractionDigits: 2 });
const PERIODE: Record<string, string> = { one_time: "einmalig", monthly: "monatlich", annual: "jährlich" };
const TYP_FARBE: Record<string, string> = { active: "blue", won: "green", lost: "gray" };

export function OpportunitiesAbschnitt({ leadId, leadName, opportunities, kontakte }: { leadId: string; leadName: string | null; opportunities: Opportunity[]; kontakte: Kontakt[] }) {
  const meta = useMeta();
  const fmt = EURO(meta.meta?.org.currency ?? "EUR");
  const darf = useDarfAendern();
  const wechseln = useStatusWechsel();
  const meldung = useMeldung();
  const [dialog, setDialog] = useState<{ opp: Opportunity | null } | null>(null);
  // Eigene Änderungen sofort zeigen; danach liefert die Lead-Seite (Echtzeit) denselben Stand
  const [lokal, setLokal] = useState<Record<string, Opportunity>>({});
  useEffect(() => setLokal({}), [opportunities]);
  const liste = opportunities.map((o) => lokal[o.id] ?? o);
  const pipelines = meta.meta?.pipelines ?? [];

  const statusPunkte = (o: Opportunity): MenuePunkt[] =>
    pipelines.flatMap((p, i) => [
      ...(pipelines.length > 1 ? [{ label: p.name, ueberschrift: true, trenner: i > 0 } as MenuePunkt] : []),
      ...p.statuses.map<MenuePunkt>((s) => ({
        label: s.label,
        icon: o.status_id === s.id ? "haken" : undefined,
        onClick: () =>
          void wechseln({ ...(o as OpportunityZeile), lead_name: leadName, contact_name: null, user_name: null, date_created: null, date_updated: null }, s.id, {
            sofort: (_x, id) => setLokal((l) => ({ ...l, [o.id]: { ...o, status_id: id, status_label: s.label, status_type: s.type as Opportunity["status_type"] } })),
            rollback: () => setLokal((l) => ({ ...l, [o.id]: o })),
            fertig: (neu) => setLokal((l) => ({ ...l, [o.id]: { ...o, ...neu } })),
          }),
      })),
    ]);

  // Einzelnes Feld der Opportunity ändern: sofort anzeigen, bei Fehler zurück (wie die Lead-Felder), Hinweise des Servers als kleine Meldung
  const speichern = async (o: Opportunity, aenderung: OpportunityAenderung) => {
    const vorher = lokal[o.id] ?? o;
    setLokal((l) => ({ ...l, [o.id]: { ...vorher, ...(aenderung as Partial<Opportunity>) } }));
    try {
      const r = await opportunitiesApi.aendern(o.id, aenderung);
      setLokal((l) => ({ ...l, [o.id]: { ...vorher, ...r.opportunity } }));
      for (const h of r.hinweise ?? []) meldung.zeige("info", h, undefined, 9000);
    } catch (e) {
      setLokal((l) => ({ ...l, [o.id]: vorher }));
      meldung.fehler(e, "Opportunity nicht gespeichert");
    }
  };

  const plus = (
    <button type="button" className="symbolknopf symbolknopf--klein" title={TL.opportunity.anlegen} aria-label={TL.opportunity.anlegen} onClick={() => setDialog({ opp: null })}>
      <Icon name="plus" groesse={14} />
    </button>
  );

  return (
    <>
      <Abschnitt id="opportunities" icon="opportunity" titel={T.lead.opportunities} zahl={liste.length} leer={liste.length === 0} leerPlus aktionenImmer aktionen={plus} standardZu={liste.length === 0}>
        {liste.length === 0 ? (
          <p className="ls-leer">
            {TL.opportunity.keine}{" "}
            <button type="button" className="textknopf" onClick={() => setDialog({ opp: null })}>
              {TL.opportunity.anlegenLink}
            </button>
          </p>
        ) : null}
        {liste.map((o) => {
          const aendern = darf(o as OpportunityZeile);
          const pille = (
            <>
              <span className="status-pille__punkt" />
              <span className="status-pille__text">{o.status_label ?? "Status"}</span>
              {aendern ? <Icon name="chevron-unten" groesse={10} /> : null}
            </>
          );
          const klasse = `status-pille status--${TYP_FARBE[o.status_type ?? "active"]} status-pille--klein`;
          return (
            <div key={o.id} className={`ls-opp ls-opp--${o.status_type ?? "active"}`}>
              <div className="ls-opp__kopf">
                {aendern ? (
                  <MenueKnopf knopfKlasse={`${klasse} ls-opp__status`} titel="Status der Opportunity ändern" punkte={statusPunkte(o)} breite={240}>
                    {pille}
                  </MenueKnopf>
                ) : (
                  <span className={klasse}>{pille}</span>
                )}
                {aendern ? (
                  <button type="button" className="symbolknopf symbolknopf--klein ls-opp__stift" title={TL.opportunity.aendern} aria-label={TL.opportunity.aendern} onClick={() => setDialog({ opp: o })}>
                    <Icon name="stift" groesse={12} />
                  </button>
                ) : null}
              </div>
              <dl className="ls-felder ls-opp__felder">
                <OppZeile label="Wert" def={{ id: "value", name: "Wert", type: "number" }} wert={o.value !== null && o.value !== undefined ? o.value / 100 : null} darf={aendern}
                  anzeige={o.value !== null && o.value !== undefined ? `${fmt.format(o.value / 100)}${o.value_period && o.value_period !== "one_time" ? ` ${PERIODE[o.value_period] ?? ""}` : ""}` : null}
                  onSpeichern={(w) => speichern(o, { value: w === null ? null : Math.round(Number(w) * 100) })} />
                <OppZeile label="Wahrscheinlichkeit" def={{ id: "confidence", name: "Wahrscheinlichkeit", type: "number" }} wert={o.confidence ?? null} darf={aendern}
                  anzeige={o.confidence !== null && o.confidence !== undefined ? `${o.confidence} %` : null}
                  onSpeichern={(w) => {
                    if (w !== null && (!Number.isFinite(Number(w)) || Number(w) < 0 || Number(w) > 100)) {
                      meldung.zeige("fehler", "Wahrscheinlichkeit: bitte eine Zahl von 0 bis 100 eingeben.");
                      return Promise.resolve();
                    }
                    return speichern(o, { confidence: w === null ? null : Math.round(Number(w)) });
                  }} />
                <OppZeile label="Abschlussdatum" def={{ id: "date_won", name: "Abschlussdatum", type: "date" }} wert={o.date_won ? o.date_won.slice(0, 10) : null} darf={aendern}
                  anzeige={o.date_won ? datumText(o.date_won) : null}
                  onSpeichern={(w) => speichern(o, { date_won: w === null ? null : String(w).slice(0, 10) })} />
                <OppZeile label="Zuständig" def={{ id: "user_id", name: "Zuständig", type: "user" }} wert={o.user_id ?? null} darf={aendern}
                  anzeige={o.user_id ? meta.nutzerName(o.user_id) : null}
                  onSpeichern={(w) => speichern(o, { user_id: w === null ? null : String(w) })} />
                <OppZeile label="Kontakt" def={{ id: "contact_id", name: "Kontakt", type: "contact" }} wert={o.contact_id ?? null} darf={aendern} kontakte={kontakte}
                  anzeige={o.contact_id ? kontakte.find((k) => k.id === o.contact_id)?.name || "Kontakt" : null}
                  onSpeichern={(w) => speichern(o, { contact_id: w === null ? null : String(w) })} />
                <OppZeile label="Notiz" def={{ id: "note", name: "Notiz", type: "textarea" }} wert={o.note ?? null} darf={aendern}
                  anzeige={o.note ? <span className="mehrzeilig">{o.note}</span> : null}
                  onSpeichern={(w) => speichern(o, { note: w === null ? null : String(w) })} />
              </dl>
            </div>
          );
        })}
      </Abschnitt>
      {dialog ? (
        <OpportunityDialog
          offen
          onSchliessen={() => setDialog(null)}
          opportunity={dialog.opp}
          lead={{ id: leadId, name: leadName }}
          onGespeichert={(neu) => setLokal((l) => ({ ...l, [neu.id]: neu }))}
        />
      ) : null}
    </>
  );
}

/** Eine Zeile der Opportunity: Bezeichnung links, Wert rechts als große Klickfläche (Hover wie bei den Lead-Feldern). Klick öffnet die
 * Eingabe; Enter oder Klick woanders speichert, Esc verwirft. Auswahlfelder (Nutzer, Kontakt) speichern mit der Wahl. */
function OppZeile({ label, def, wert, anzeige, darf, kontakte = [], onSpeichern }: {
  label: string;
  def: Pick<FeldDef, "id" | "name" | "type">;
  wert: FeldWert;
  anzeige: ReactNode;
  darf: boolean;
  kontakte?: Kontakt[];
  onSpeichern: (w: FeldWert) => Promise<void> | void;
}) {
  const [aktiv, setAktiv] = useState(false);
  const [entwurf, setEntwurf] = useState<FeldWert>(null);
  const offen = useRef(false);
  const sofort = def.type === "user" || def.type === "contact";
  const starten = () => {
    if (!darf) return;
    setEntwurf(wert);
    offen.current = true;
    setAktiv(true);
  };
  const beenden = () => {
    offen.current = false;
    setAktiv(false);
  };
  const fertig = (w: FeldWert) => {
    if (!offen.current) return;
    beenden();
    const neu: FeldWert = istLeer(w) ? null : w;
    if (JSON.stringify(neu) === JSON.stringify(istLeer(wert) ? null : wert)) return;
    void onSpeichern(neu);
  };
  const feldDef: FeldDef = { ...def, accepts_multiple_values: false, choices: null, description: null };
  return (
    <div className={`ls-feld ls-opp__feld${aktiv ? " ls-feld--aktiv" : ""}`} data-opp-feld={def.id}>
      <dt>{label}</dt>
      <dd>
        {aktiv ? (
          <div
            className="ls-feld__inline"
            onBlur={(e) => {
              if (sofort || e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              fertig(entwurf);
            }}
          >
            {def.type === "textarea" ? (
              <textarea
                className="eingabe eingabe--text"
                rows={3}
                autoFocus
                value={typeof entwurf === "string" ? entwurf : ""}
                onChange={(e) => setEntwurf(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    beenden();
                  } else if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    fertig(entwurf);
                  }
                }}
              />
            ) : (
              <FeldEingabe def={feldDef} wert={entwurf} kontakte={kontakte} darstellung="inline" autoFokus onChange={setEntwurf} onFertig={(w) => fertig(w !== undefined ? w : entwurf)} onAbbrechen={beenden} />
            )}
          </div>
        ) : (
          <button type="button" className={`ls-feld__wert${darf ? "" : " ls-feld__wert--gesperrt"}`} disabled={!darf} title={darf ? `${label} ändern` : undefined} onClick={starten}>
            {anzeige ? anzeige : <span className="ls-feld__leer">{darf ? "Hinzufügen …" : "—"}</span>}
          </button>
        )}
      </dd>
    </div>
  );
}
